import { z } from 'zod';
import { muscleGroups } from '../../db/types';
import { parseLocalDate } from '../utils/date';

// Swift Foundation accepts canonical UUID text even when its version/variant bits
// are not RFC-generated (the native seed catalogue intentionally uses such IDs).
// Preserve those stable identifiers while still rejecting non-UUID-shaped input.
const uuid = z.string().regex(
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  'Некорректный UUID'
);
const isoDateTime = z.string().datetime({ offset: true });
const nullableIsoDateTime = isoDateTime.nullable();
const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
  try { parseLocalDate(value); return true; } catch { return false; }
}, 'Некорректная календарная дата');
const nullableText = z.string().max(10_000).nullable();
const timestampFields = { id: uuid, createdAt: isoDateTime, updatedAt: isoDateTime };
const feedbackReason = z.enum(['sleep', 'time', 'equipment', 'technique', 'pain', 'unknown']);
const priority = z.enum(['core', 'additional', 'optional']);
const minutes = z.union([z.literal(40), z.literal(60), z.literal(90)]);
export const recommendationSchema = z.strictObject({
  version: z.literal(1), state: z.enum(['calibration', 'ready', 'hold', 'increase', 'recalibrate', 'pain']),
  confidence: z.enum(['low', 'medium', 'high']), exposures: z.number().int().nonnegative(),
  weightKg: z.number().finite().min(0).max(1_000_000).nullable(),
  repetitions: z.array(z.number().int().positive().max(1_000_000)).max(1000),
  reason: z.string().max(2000), askRir: z.boolean(), reviewDue: z.boolean()
});
export const userProfileSchema = z.strictObject({
  ...timestampFields, id: z.literal('00000000-0000-0000-0000-000000000003'), goal: z.literal('hypertrophy'), experience: z.enum(['beginner', 'experienced']),
  daysPerWeek: z.number().int().min(1).max(7), availableMinutes: minutes,
  equipment: z.enum(['gym', 'limited']), avoidAxialLoad: z.boolean(),
  excludedExerciseIds: z.array(uuid).max(25000), medicalNotes: z.string().max(10000),
  recommendationsEnabled: z.boolean(), onboardingCompletedAt: isoDateTime
});
export const exercisePreferenceSchema = z.strictObject({
  ...timestampFields, exerciseId: uuid, equipmentKey: z.string().trim().min(1).max(100),
  incrementKg: z.number().finite().positive().max(100),
  recalibrateAfter: nullableIsoDateTime, painResolvedAt: nullableIsoDateTime
});
export const bodyMeasurementSchema = z.strictObject({
  ...timestampFields, date: localDate, weightKg: z.number().finite().min(20).max(500)
});

export const exerciseSchema = z.strictObject({
  ...timestampFields,
  name: z.string().trim().min(1).max(200),
  normalizedName: z.string().min(1).max(200),
  muscleGroup: z.enum(muscleGroups),
  isCustom: z.boolean(),
  isArchived: z.boolean()
});

export const workoutPlanSchema = z.strictObject({
  ...timestampFields,
  name: z.string().trim().min(1).max(200),
  notes: nullableText,
  isArchived: z.boolean(),
  templateKey: z.enum(['full-body-a', 'full-body-b']).optional()
});

const optionalPositiveInt = z.number().int().positive().nullable();
const optionalWeight = z.number().finite().min(0).max(1_000_000).nullable();

export const planExerciseSchema = z.strictObject({
  ...timestampFields,
  planId: uuid,
  exerciseId: uuid,
  orderIndex: z.number().int().min(0),
  targetSets: optionalPositiveInt,
  targetRepsMin: optionalPositiveInt,
  targetRepsMax: optionalPositiveInt,
  targetWeightKg: optionalWeight,
  notes: nullableText,
  priority: priority.optional()
}).refine(item => item.targetRepsMin === null || item.targetRepsMax === null || item.targetRepsMin <= item.targetRepsMax, {
  message: 'Минимум повторений больше максимума'
});

export const workoutSessionSchema = z.strictObject({
  ...timestampFields,
  workoutDate: localDate,
  startedAt: nullableIsoDateTime,
  finishedAt: nullableIsoDateTime,
  status: z.enum(['active', 'completed']),
  sourcePlanId: uuid.nullable(),
  planNameSnapshot: z.string().max(200).nullable(),
  notes: nullableText,
  restEndsAt: nullableIsoDateTime,
  restPausedSeconds: z.number().int().min(0).max(900).nullable(),
  restSelectedSeconds: z.number().int().min(15).max(900).multipleOf(15),
  availableMinutes: minutes.optional()
});

export const workoutExerciseSchema = z.strictObject({
  ...timestampFields,
  workoutSessionId: uuid,
  exerciseId: uuid.nullable(),
  exerciseIdSnapshot: uuid,
  exerciseNameSnapshot: z.string().trim().min(1).max(200),
  muscleGroupSnapshot: z.enum(muscleGroups),
  targetSetsSnapshot: optionalPositiveInt,
  targetRepsMinSnapshot: optionalPositiveInt,
  targetRepsMaxSnapshot: optionalPositiveInt,
  targetWeightKgSnapshot: optionalWeight,
  orderIndex: z.number().int().min(0),
  notes: nullableText,
  equipmentKey: z.string().max(100).optional(),
  feedbackReason: feedbackReason.optional(),
  recommendation: recommendationSchema.optional()
});

export const setEntrySchema = z.strictObject({
  ...timestampFields,
  workoutExerciseId: uuid,
  orderIndex: z.number().int().min(0),
  repetitions: z.number().int().min(0).max(1_000_000).nullable(),
  weightKg: optionalWeight,
  isCompleted: z.boolean(),
  completedAt: nullableIsoDateTime,
  kind: z.enum(['warmup', 'working']).optional(),
  rir: z.number().int().min(0).max(3).nullable().optional(),
  pain: z.boolean().optional()
}).refine(item => !item.isCompleted || (item.repetitions !== null && item.repetitions > 0), {
  message: 'Завершённый подход не содержит повторений'
});

export const scheduledWorkoutSchema = z.strictObject({
  ...timestampFields,
  scheduledDate: localDate,
  reminderTime: z.string().refine(value => value === '' || /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value), 'Некорректное время'),
  planId: uuid,
  planNameSnapshot: z.string().trim().min(1).max(200),
  status: z.enum(['scheduled', 'started', 'completed', 'cancelled']),
  linkedWorkoutSessionId: uuid.nullable(),
  calendarExportedAt: nullableIsoDateTime
});

export const appSettingsSchema = z.strictObject({
  ...timestampFields,
  defaultRestSeconds: z.number().int().min(15).max(900).multipleOf(15),
  lastScheduledReminderTime: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
  theme: z.enum(['system', 'light', 'dark']),
  displayName: z.string().trim().max(60).optional(),
  heightCm: z.number().int().min(80).max(250).nullable().optional(),
  avatarDataUrl: z.string().startsWith('data:image/jpeg;base64,').max(250_000).nullable().optional(),
  lastBackupAt: nullableIsoDateTime,
  storagePersistenceRequestedAt: nullableIsoDateTime,
  onboardingDismissed: z.boolean()
});

export const appMetadataSchema = z.strictObject({
  ...timestampFields,
  schemaVersion: z.number().int().positive(),
  appVersion: z.string().min(1).max(50),
  firstOpenedAt: isoDateTime,
  seedVersion: z.number().int().nonnegative()
});

const uniqueIds = <T extends { id: string }>(items: T[]) => new Set(items.map(item => item.id)).size === items.length;
const uniqueOrder = <T extends { orderIndex: number }>(items: T[]) => new Set(items.map(item => item.orderIndex)).size === items.length;

export const databaseSnapshotSchema = z.strictObject({
  exercises: z.array(exerciseSchema).max(25_000),
  workoutPlans: z.array(workoutPlanSchema).max(10_000),
  planExercises: z.array(planExerciseSchema).max(100_000),
  workoutSessions: z.array(workoutSessionSchema).max(100_000),
  workoutExercises: z.array(workoutExerciseSchema).max(500_000),
  setEntries: z.array(setEntrySchema).max(2_000_000),
  scheduledWorkouts: z.array(scheduledWorkoutSchema).max(100_000),
  appSettings: z.array(appSettingsSchema).max(1),
  appMetadata: z.array(appMetadataSchema).max(1),
  userProfiles: z.array(userProfileSchema).max(1).default([]),
  exercisePreferences: z.array(exercisePreferenceSchema).max(25000).default([]),
  bodyMeasurements: z.array(bodyMeasurementSchema).max(100000).default([])
}).superRefine((data, context) => {
  const collections = Object.entries(data) as Array<[string, Array<{ id: string }>] >;
  for (const [name, items] of collections) {
    if (!uniqueIds(items)) context.addIssue({ code: 'custom', message: `Повторяются UUID: ${name}` });
  }
  const exerciseIds = new Set(data.exercises.map(item => item.id));
  const planIds = new Set(data.workoutPlans.map(item => item.id));
  const sessionIds = new Set(data.workoutSessions.map(item => item.id));
  const workoutExerciseIds = new Set(data.workoutExercises.map(item => item.id));
  const normalizedNames = data.exercises.map(item => item.normalizedName);
  if (new Set(data.bodyMeasurements.map(item => item.date)).size !== data.bodyMeasurements.length) context.addIssue({ code: 'custom', message: 'Повторяется дата измерения массы' });
  if (new Set(data.exercisePreferences.map(item => item.exerciseId)).size !== data.exercisePreferences.length) context.addIssue({ code: 'custom', message: 'Повторяются настройки упражнения' });
  for (const item of data.exercisePreferences) {
    if (!exerciseIds.has(item.exerciseId)) context.addIssue({ code: 'custom', message: 'Настройки ссылаются на неизвестное упражнение' });
  }
  for (const profile of data.userProfiles) {
    if (profile.excludedExerciseIds.some(id => !exerciseIds.has(id))) context.addIssue({ code: 'custom', message: 'Ограничение ссылается на неизвестное упражнение' });
  }
  if (new Set(normalizedNames).size !== normalizedNames.length) context.addIssue({ code: 'custom', message: 'Повторяются названия упражнений' });
  for (const item of data.planExercises) {
    if (!planIds.has(item.planId) || !exerciseIds.has(item.exerciseId)) context.addIssue({ code: 'custom', message: 'Нарушена связь упражнения плана' });
  }
  for (const plan of data.workoutPlans) {
    if (!uniqueOrder(data.planExercises.filter(item => item.planId === plan.id))) context.addIssue({ code: 'custom', message: 'Повторяется порядок упражнений плана' });
  }
  for (const item of data.workoutExercises) {
    if (!sessionIds.has(item.workoutSessionId) || !exerciseIds.has(item.exerciseIdSnapshot) || (item.exerciseId !== null && !exerciseIds.has(item.exerciseId))) {
      context.addIssue({ code: 'custom', message: 'Нарушена связь упражнения тренировки' });
    }
  }
  for (const session of data.workoutSessions) {
    if (!uniqueOrder(data.workoutExercises.filter(item => item.workoutSessionId === session.id))) context.addIssue({ code: 'custom', message: 'Повторяется порядок упражнений тренировки' });
  }
  for (const item of data.setEntries) {
    if (!workoutExerciseIds.has(item.workoutExerciseId)) context.addIssue({ code: 'custom', message: 'Подход ссылается на неизвестное упражнение' });
  }
  for (const exercise of data.workoutExercises) {
    if (!uniqueOrder(data.setEntries.filter(item => item.workoutExerciseId === exercise.id))) context.addIssue({ code: 'custom', message: 'Повторяется порядок подходов' });
  }
  for (const item of data.scheduledWorkouts) {
    if (!planIds.has(item.planId)) context.addIssue({ code: 'custom', message: 'Расписание ссылается на неизвестный план' });
    if (item.linkedWorkoutSessionId !== null && !sessionIds.has(item.linkedWorkoutSessionId)) context.addIssue({ code: 'custom', message: 'Расписание ссылается на неизвестную тренировку' });
  }
  if (data.workoutSessions.filter(item => item.status === 'active').length > 1) context.addIssue({ code: 'custom', message: 'В копии больше одной активной тренировки' });
});

export const backupManifestSchema = z.strictObject({
  format: z.literal('trenirovki-pwa-backup'),
  schemaVersion: z.number().int().positive().max(2),
  appVersion: z.string().min(1).max(50),
  createdAt: isoDateTime,
  locale: z.literal('ru-RU'),
  weightUnit: z.literal('kg'),
  counts: z.record(z.string(), z.number().int().nonnegative()),
  dataSha256: z.string().regex(/^[a-f0-9]{64}$/)
});

export type ValidatedDatabaseSnapshot = z.infer<typeof databaseSnapshotSchema>;
export type BackupManifest = z.infer<typeof backupManifestSchema>;
